/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Flex,
	Text,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../../../ews-template-labels';
import Loading from '../../../../../../components/Loading';
import useStore from '../../../../../../store';
import { getYear } from '../../../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const Q_REPORT = gql`
	query getEarlyWarningNonLifeRSRatioReport($year: Float!, $companyType: InsuranceInstitutionsType!, $companyId: String) {
		getEarlyWarningNonLifeRSRatioReport(year: $year, companyType: $companyType, companyId: $companyId)
	}
`;

const M_SAVE_NONLIFE_RS_RATIO = gql`
	mutation saveCalculatedNonLifeRSRatioStatement(
		$year: Float!
		$companyType: InsuranceInstitutionsType!
		$companyId: String
		$requiredSolvency: Float!
		$companyRiskRanking: Float!
	) {
		saveCalculatedNonLifeRSRatioStatement(
			year: $year
			companyType: $companyType
			companyId: $companyId
			requiredSolvency: $requiredSolvency
			companyRiskRanking: $companyRiskRanking
		)
	}
`;

export default function RatioNonLifeRequiredSolvencySpreadSheetTable({
	year = null,
	companyId = null,
	companyRiskRankingData = null,
	requiredSolvencyExport = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const currentLang = useStore((state) => state?.currentLang);
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const companyType = urlParams.get('companyType') || null;
	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState(null);
	const [q1Cells, setQ1Cells] = useState([]);
	const [initialQ1Cells, setInitialQ1Cells] = useState([]);
	const [isEditing, setIsEditing] = useState(false);
	const [showEditField, setShowEditField] = useState(false);

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);
	const [saveRequiredSolvency, { loading: loadingSaveRS }] = useMutation(M_SAVE_NONLIFE_RS_RATIO, {
		onCompleted: () => {
			toast({
				title: t('Required Solvency saved successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
			load();
		},
		onError: (err) => {
			const message = err?.graphQLErrors?.[0]?.message || err?.message || 'something went wrong';
			toast({
				title: t(message),
				status: 'error',
				isClosable: true,
				position: 'bottom-right',
			});
		},
	});

	useEffect(load, [year, companyType, companyId]);

	const allowEditUser = () => {
		if (!user?.role) return;

		const roles = user.role.split(',').map((r) => r.trim());
		const ews = user.ews || [];

		let showEdit = false;

		if (roles.includes('admin')) {
			showEdit = true;
		} else if (roles.includes('ews')) {
			showEdit = ews.includes('ewsCanViewStatementGeneralInsurance') || ews.includes('ewsCanViewStatementMicroInsurance');
		}

		setShowEditField(showEdit);
	};

	useEffect(() => {
		allowEditUser();
	}, [user]);

	useEffect(() => {
		if (data?.getEarlyWarningNonLifeRSRatioReport) {
			setDetail(data?.getEarlyWarningNonLifeRSRatioReport);
		}
	}, [data]);

	useEffect(() => {
		if (detail) {
			quarterDetail(detail);
		}
	}, [detail]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error.graphQLErrors.forEach(({ message }) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message || 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { year: getYear(year), companyType, companyId };
		loadReport({ variables });
	}

	function emptyCells(labels = [], { readOnly = false } = {}) {
		return labels?.map(() => [{ value: '', readOnly }]);
	}

	function quarterDetail(detailData) {
		if (!detailData?.labels?.length) return;

		let requiredSolvency = [];

		if (detailData?.q1 && detailData?.q1?.length) {
			requiredSolvency = quarterCells({ labels: detailData?.labels, quarter: detailData?.q1 });
		} else if (detailData?.requiredSolvency) {
			requiredSolvency = detailData?.labels?.map(() => [{ value: detailData?.requiredSolvency, readOnly: !showEditField }]);
		} else {
			requiredSolvency = emptyCells(detailData?.labels, { readOnly: false });
		}

		const numRows = detailData?.labels.length;
		const normalized =
			requiredSolvency?.length === numRows
				? requiredSolvency
				: [...requiredSolvency, ...Array(numRows - requiredSolvency?.length).fill([{ value: '', readOnly: false }])];

		setQ1Cells(normalized);
		setInitialQ1Cells(normalized);
		setIsEditing(false);

		requiredSolvencyExport({ labels: detailData?.labels, q1Cells: normalized, requiredSolvency: detailData?.requiredSolvency });
	}

	function quarterCells({ labels = [], quarter = [] }) {
		return labels?.map((label) => {
			const rowData = quarter?.find((item) => item?.code === label?.code);

			if (!rowData || !Array.isArray(rowData.values)) {
				return [{ value: '', readOnly: false }];
			}

			return rowData?.values?.map((cell) => {
				const numeric = typeof cell?.value === 'number';
				return {
					value: numeric ? truncateDecimal(cell.value, 2) : cell.value,
					readOnly: cell?.readOnly ?? true,
				};
			});
		});
	}

	async function saveButton() {
		if (!q1Cells?.length) return;

		const value = q1Cells[0][0]?.value;
		const crrData = companyRiskRankingData?.q1Cells?.[0]?.[0]?.value;

		if (value === undefined || value === null || value === '') {
			toast({
				title: t('Required Solvency cannot be empty'),
				status: 'warning',
				isClosable: true,
				position: 'top-right',
			});
			return;
		}

		const variables = {
			year: getYear(year),
			companyType,
			companyId,
			requiredSolvency: parseFloat(value),
			companyRiskRanking: parseFloat(crrData),
		};

		await saveRequiredSolvency({ variables });

		const updatedQ1Cells = detail?.labels?.map(() => [{ value, readOnly: false }]);
		setQ1Cells(updatedQ1Cells);
		setInitialQ1Cells(updatedQ1Cells);
		setIsEditing(false);

		setDetail((prev) => ({
			...prev,
			q1: detail?.labels?.map((label) => ({
				code: label?.code,
				values: [{ value, readOnly: false }],
			})),
		}));

		requiredSolvencyExport({ labels: detail?.labels, q1Cells: updatedQ1Cells });
	}

	function truncateDecimal(value, digits = 3) {
		if (typeof value !== 'number') return value;
		const factor = 10 ** digits;
		return Math.trunc(value * factor) / factor;
	}

	const updatedRef = useRef(null);

	function updatedExport(cells) {
		clearTimeout(updatedRef.current);
		updatedRef.current = setTimeout(() => {
			requiredSolvencyExport({ labels: detail?.labels, q1Cells: cells });
		}, 300);
	}

	return (
		<Box>
			{loading && <Loading />}

			{!loading && !detail && (
				<Text color="gray.400" textAlign="center" mt={4}>
					{t('No Data')}
				</Text>
			)}

			{!loading && detail && (
				<Box className="ews-container">
					<EWSLabels type="EWS" labels={detail.labels} active={active} customBackgroundColor="#f7fafc" />

					<Flex>
						{q1Cells?.length > 0 && (
							<>
								<Spreadsheet
									data={q1Cells}
									onChange={(newCells) => {
										setQ1Cells(newCells);

										const changed = newCells?.some((row, rIndex) =>
											row?.some((cell, cIndex) => cell.value !== initialQ1Cells[rIndex]?.[cIndex]?.value),
										);
										setIsEditing(changed);
										updatedExport(newCells);
									}}
								/>
								<Flex ml="50px">
									{isEditing && showEditField && (
										<>
											<Button
												variant="solid"
												size="50px"
												ml={2}
												p={0}
												pr={2}
												bg="green"
												_hover={{ bg: 'darkgreen' }}
												color="white"
												rightIcon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 15 }} />}
												onClick={onOpen}
												isLoading={loadingSaveRS}
												spinnerPlacement="start"
											/>

											<Button
												variant="solid"
												size="50px"
												ml={2}
												p={0}
												pr={2}
												bg="red"
												_hover={{ bg: 'darkred' }}
												color="white"
												rightIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 15 }} />}
												onClick={() => {
													setQ1Cells(initialQ1Cells);
													setIsEditing(false);
												}}
												isLoading={loadingSaveRS}
												spinnerPlacement="start"
											/>
										</>
									)}
								</Flex>
							</>
						)}
					</Flex>
				</Box>
			)}

			<AlertDialog isOpen={isOpen} leastDestructiveRef={undefined} onClose={onClose}>
				<AlertDialogOverlay />
				<AlertDialogContent>
					<AlertDialogHeader fontSize="lg" fontWeight="bold">
						{t('Confirm Required Solvency')}
					</AlertDialogHeader>

					<AlertDialogBody>
						<Text fontSize="16px">{t('Do you want to input this required solvency value?')}</Text>
					</AlertDialogBody>

					<AlertDialogFooter>
						<Button onClick={onClose}>{t('Cancel')}</Button>
						<Button
							colorScheme="green"
							onClick={() => {
								saveButton();
								onClose();
							}}
							ml={3}
						>
							{t('Confirm')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Box>
	);
}
